/**
 * Iter 66 — useMajorDelete hook.
 *
 * Replaces ad-hoc `window.confirm` + `apiDel(...)` patterns for the 12
 * major entity types governed by the approval workflow. Behaviour:
 *
 *   1. Opens a modal asking for a deletion REASON (mandatory, min 10 chars).
 *   2. Posts the request to `/delete-requests`.
 *   3. If the server replies `{auto_approved: true}` (super-admin path or
 *      a non-major entity), the caller's `directDelete` callback runs
 *      immediately and the page refreshes.
 *   4. Otherwise the request was filed to the user's Reporting Manager —
 *      we toast a success message naming the approver and DO NOT delete
 *      yet. The actual archive happens server-side once the RM approves.
 *
 * Usage:
 *   const [requestDelete, MajorDeleteHost] = useMajorDelete();
 *   await requestDelete({
 *     entity_type: 'project',
 *     entity_id: p.id,
 *     entity_name: p.name,
 *     contract_value: p.contract_value,
 *     directDelete: () => apiDel(`/projects/${p.id}`),
 *     onSuccess: load,                       // refresh list afterwards
 *   });
 *   ...
 *   return <>{MajorDeleteHost}<YourPage /></>;
 */
import React, { useCallback, useState } from 'react';
import { AlertTriangle, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { apiPost } from '../lib/api';

const ENTITY_LABEL = {
  project:         'Project',
  employee:        'Employee',
  tender:          'Tender',
  user:            'User',
  po:              'Purchase Order',
  invoice:         'Invoice',
  ra_bill:         'RA Bill',
  payroll:         'Payroll Record',
  attendance:      'Attendance Record',
  face_enrollment: 'Face Enrolment',
  boq:             'BOQ',
  labour:          'Labour Record',
};

function MajorDeleteDialog({ open, ctx, onCancel, onSubmit, submitting }) {
  const [reason, setReason] = useState('');
  if (!open) return null;
  const label = ENTITY_LABEL[ctx.entity_type] || ctx.entity_type;
  const ok = reason.trim().length >= 10;

  return (
    <div
      className="fixed inset-0 z-[1000] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
      data-testid="major-delete-backdrop"
    >
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden" data-testid="major-delete-dialog">
        <header className="px-5 py-3 bg-rose-50 border-b border-rose-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle size={16} className="text-rose-600" />
            <h2 className="font-display font-bold text-sm uppercase tracking-wide">Request {label} Deletion</h2>
          </div>
          <button onClick={onCancel} className="text-slate-400 hover:text-slate-700" aria-label="Close" data-testid="major-delete-close">
            <X size={16} />
          </button>
        </header>
        <div className="px-5 py-4 text-sm text-slate-700">
          <p>
            You are about to request deletion of:<br />
            <strong>{label}: {ctx.entity_name || ctx.entity_id}</strong>
            {ctx.contract_value ? <span className="block text-rose-700 mt-0.5">Contract value: ₹{Number(ctx.contract_value).toLocaleString('en-IN')}</span> : null}
          </p>
          <p className="text-xs text-slate-500 mt-3">
            Major-entity deletions require your Reporting Manager's approval. The record will be archived (not hard-deleted)
            once they approve. <strong>This cannot be reversed without restoring from a backup.</strong>
          </p>
          <label className="text-[10px] uppercase tracking-wider font-display font-bold text-slate-500 mt-4 block">
            Reason for deletion <span className="text-rose-600">*</span>
            <span className="text-[10px] text-slate-400 ml-2 normal-case font-normal">min 10 characters</span>
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            className="w-full mt-1 border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-rose-500"
            placeholder="e.g. Duplicate entry / project cancelled by client / data-entry error..."
            data-testid="major-delete-reason"
            autoFocus
          />
          <div className="text-[10px] text-slate-400 mt-1 text-right">{reason.trim().length}/10</div>
        </div>
        <footer className="px-5 py-3 bg-slate-50 border-t border-slate-200 flex items-center justify-end gap-2">
          <button onClick={onCancel} disabled={submitting} className="px-4 py-2 text-xs font-display font-bold uppercase tracking-wider text-slate-700 hover:bg-slate-200 rounded-sm" data-testid="major-delete-cancel">
            Cancel
          </button>
          <button
            onClick={() => onSubmit(reason.trim())}
            disabled={!ok || submitting}
            className="px-4 py-2 text-xs font-display font-bold uppercase tracking-wider text-white bg-rose-600 hover:bg-rose-700 rounded-sm disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
            data-testid="major-delete-submit"
          >
            {submitting ? <Loader2 size={11} className="animate-spin" /> : null}
            {submitting ? 'Submitting…' : 'Submit Request'}
          </button>
        </footer>
      </div>
    </div>
  );
}

export function useMajorDelete() {
  const [state, setState] = useState({ open: false, ctx: {}, submitting: false });

  const requestDelete = useCallback((ctx) => {
    return new Promise((resolve) => {
      setState({ open: true, ctx, submitting: false, _resolve: resolve });
    });
  }, []);

  const cancel = () => {
    state._resolve?.({ ok: false, archived: false });
    setState({ open: false, ctx: {}, submitting: false });
  };

  const submit = async (reason) => {
    setState((s) => ({ ...s, submitting: true }));
    const ctx = state.ctx;
    try {
      const r = await apiPost('/delete-requests', {
        entity_type: ctx.entity_type,
        entity_id: ctx.entity_id,
        entity_name: ctx.entity_name,
        contract_value: ctx.contract_value || null,
        reason,
      });
      if (r?.auto_approved) {
        // Super-admin bypass — actually delete
        if (typeof ctx.directDelete === 'function') {
          await ctx.directDelete();
        }
        toast.success('Deleted (super-admin bypass)');
        state._resolve?.({ ok: true, archived: true, autoApproved: true });
      } else {
        toast.success(`Request submitted to ${r.approver_name} for approval`, {
          description: 'You\'ll be notified once they approve or reject.',
        });
        state._resolve?.({ ok: true, archived: false, request: r });
      }
      setState({ open: false, ctx: {}, submitting: false });
      ctx.onSuccess?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Request failed');
      setState((s) => ({ ...s, submitting: false }));
    }
  };

  const host = (
    <MajorDeleteDialog
      open={state.open}
      ctx={state.ctx}
      submitting={state.submitting}
      onCancel={cancel}
      onSubmit={submit}
    />
  );

  return [requestDelete, host];
}
